import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import { Check, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const plans = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Perfect for trying out our tools',
    features: [
      { text: 'Access to free tools', included: true },
      { text: 'Basic support', included: true },
      { text: '100 uses per month', included: true },
      { text: 'Pro tools access', included: false },
      { text: 'Business tools access', included: false },
      { text: 'Priority support', included: false },
      { text: 'API access', included: false },
    ],
    cta: 'Current Plan',
    popular: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$19',
    period: '/month',
    description: 'For professionals and creators',
    features: [
      { text: 'Access to free tools', included: true },
      { text: 'Access to Pro tools', included: true },
      { text: 'Unlimited uses', included: true },
      { text: 'Email support', included: true },
      { text: 'Business tools access', included: false },
      { text: 'Priority support', included: false },
      { text: 'API access', included: false },
    ],
    cta: 'Upgrade to Pro',
    popular: true,
  },
  {
    id: 'business',
    name: 'Business',
    price: '$49',
    period: '/month',
    description: 'For teams and enterprises',
    features: [
      { text: 'Access to all tools', included: true },
      { text: 'Unlimited uses', included: true },
      { text: 'Priority support', included: true },
      { text: 'API access', included: true },
      { text: 'Team collaboration', included: true },
      { text: 'Custom integrations', included: true },
      { text: 'Dedicated account manager', included: true },
    ],
    cta: 'Upgrade to Business',
    popular: false,
  },
];

const PricingPage = () => {
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(null);

  const handleUpgrade = async (planId) => {
    if (!user) {
      login();
      return;
    }

    setLoading(planId);
    try {
      // Mock subscription update
      await axios.post(
        `${API}/user/subscription`,
        { plan: planId },
        { withCredentials: true }
      );
      toast.success(`Successfully upgraded to ${planId} plan!`);
      window.location.reload();
    } catch (error) {
      toast.error('Failed to update subscription');
    } finally {
      setLoading(null);
    }
  };

  return (
    <>
      <SEOHead
        title="Pricing - Free, Pro & Business Plans | ToolsForge"
        description="Choose the perfect plan for your needs. Start free with basic tools, or upgrade to Pro or Business for unlimited access to all AI tools and utilities."
        keywords="toolsforge pricing, free tools, pro plan, business plan, tool subscription"
      />
      <div className="min-h-screen pt-24 pb-16 px-6" data-testid="pricing-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 font-['Plus_Jakarta_Sans']">
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Choose the plan that fits your needs. Upgrade or downgrade at any time.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 items-start" data-testid="pricing-cards">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`relative flex flex-col p-8 rounded-2xl border ${
                plan.popular 
                  ? 'pricing-popular border-primary/50' 
                  : 'border-white/10 bg-black/40'
              } backdrop-blur-sm`}
              data-testid={`pricing-${plan.id}`}
            >
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white">
                  Most Popular
                </Badge>
              )}

              <div className="mb-6">
                <h3 className="text-xl font-semibold mb-2 font-['Plus_Jakarta_Sans']">{plan.name}</h3>
                <p className="text-sm text-muted-foreground">{plan.description}</p>
              </div>

              <div className="mb-6">
                <span className="text-4xl font-bold">{plan.price}</span>
                <span className="text-muted-foreground">{plan.period}</span>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    {feature.included ? (
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <X className="w-5 h-5 text-muted-foreground/50 flex-shrink-0" />
                    )}
                    <span className={feature.included ? '' : 'text-muted-foreground/50'}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => handleUpgrade(plan.id)}
                disabled={user?.subscription === plan.id || loading === plan.id}
                className={plan.popular ? 'glow-blue' : ''}
                variant={plan.popular ? 'default' : 'outline'}
                data-testid={`select-${plan.id}`}
              >
                {loading === plan.id ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </span>
                ) : user?.subscription === plan.id ? (
                  'Current Plan'
                ) : (
                  plan.cta
                )}
              </Button>
            </motion.div>
          ))}
        </div>

        {/* FAQ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <p className="text-muted-foreground">
            Questions? <a href="#" className="text-primary hover:underline">Contact our sales team</a>
          </p>
        </motion.div>
      </div>
      </div>
    </>
  );
};

export default PricingPage;
